import { useEffect, useRef, useState } from 'react';
import type { FC, FormEvent } from 'react';

import { useBreakpoint, useI18n } from '../../hooks';
import Icon from '../Icon';
import SearchInput from '../SearchInput';

import NavigationListItemWrapper from './NavigationListItemWrapper';
import type { AppShellProps, NavListItemWrapperProps } from './AppShell.types';
import {
  StyledAppShellSearchButton,
  StyledAppshellSearchContainer,
  StyledAppShellSearchDecoration,
  StyledSearch
} from './AppShellSearch.styles';

export interface AppShellSearchProps {
  collapsed: boolean;
  searchLabel: string;
  searchInput: AppShellProps['searchInput'];
}

export const AppShellSearch: FC<AppShellSearchProps> = ({
  collapsed,
  searchLabel,
  searchInput
}) => {
  const btnElRef = useRef<HTMLButtonElement>(null);
  const formElRef = useRef<HTMLFormElement>(null);
  const searchRef = useRef<HTMLInputElement>();
  const t = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const isMediumOrAbove = useBreakpoint('md');

  const onMenuToggle: NavListItemWrapperProps['onMenuToggle'] = state => {
    setMenuOpen(state === 'open');
  };

  useEffect(() => {
    const onTab = (e: KeyboardEvent) => {
      if (menuOpen && e.key === 'Tab') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    btnElRef.current?.addEventListener('keydown', onTab);
    return () => {
      btnElRef.current?.removeEventListener('keydown', onTab);
    };
  }, [searchRef, menuOpen]);

  const searchFormProps = {
    role: 'search',
    'aria-label': searchLabel,
    onSubmit: (e: FormEvent) => e.preventDefault()
  };

  return (
    <StyledSearch collapsed={collapsed} isMediumOrAbove={isMediumOrAbove}>
      {collapsed ? (
        <NavigationListItemWrapper
          childElementRef={formElRef}
          label={t('search_placeholder_default')}
          tooltipLabel={t('search_placeholder_default')}
          onMenuToggle={onMenuToggle}
          content={
            <StyledAppshellSearchContainer>
              <SearchInput {...searchInput} resultsPopover={false} ref={searchRef} />
            </StyledAppshellSearchContainer>
          }
        >
          <form {...searchFormProps} ref={formElRef}>
            <StyledAppShellSearchButton aria-label={t('search')} ref={btnElRef}>
              <StyledAppShellSearchDecoration>
                <Icon name='search' />
              </StyledAppShellSearchDecoration>
            </StyledAppShellSearchButton>
          </form>
        </NavigationListItemWrapper>
      ) : (
        <form {...searchFormProps}>
          <SearchInput {...searchInput} />
        </form>
      )}
    </StyledSearch>
  );
};
